import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import transactionService from '../../services/transactionService';
import currencyService from '../../services/currencyService';
import { Button, Modal } from '../common';
import toast from 'react-hot-toast';

const TransactionForm = ({ isOpen, onClose, onSuccess, initialData = null }) => {
  const { t } = useTranslation();
  const [exchangeRates, setExchangeRates] = useState([]);
  const [loading, setLoading] = useState(false);

  // Mode: 'sell' = customer sells USD (gives USD, gets IQD)
  //       'buy' = customer buys USD (gives IQD, gets USD)
  const [mode, setMode] = useState('sell');

  // Input mode: 'give' = enter what customer gives, 'receive' = enter what customer receives
  const [inputMode, setInputMode] = useState('give');

  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    rate: '',
    amount: '',
    notes: ''
  });

  const [usdCurrency, setUsdCurrency] = useState(null);
  const [iqdCurrency, setIqdCurrency] = useState(null);
  const [marketRate, setMarketRate] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchCurrencies();
      fetchExchangeRates();
      if (initialData) {
        const isSellingUsd = initialData.currencyInId === usdCurrency?.id;
        setMode(isSellingUsd ? 'sell' : 'buy');
        setFormData({
          customerName: initialData.customerName || '',
          customerPhone: initialData.customerPhone || '',
          rate: initialData.exchangeRate?.toString() || '',
          amount: initialData.amountIn?.toString() || '',
          notes: ''
        });
      } else {
        resetForm();
      }
    }
  }, [isOpen, initialData]);

  // Auto-populate rate when exchange rates load or mode changes
  useEffect(() => {
    if (usdCurrency && iqdCurrency && exchangeRates.length > 0) {
      const rateData = exchangeRates.find(
        r => r.fromCurrencyId === usdCurrency.id && r.toCurrencyId === iqdCurrency.id
      );
      if (rateData) {
        const selectedRate = mode === 'sell' ? rateData.buyRate : rateData.sellRate;
        setFormData(prev => ({
          ...prev,
          rate: selectedRate?.toString() || rateData.rate?.toString() || ''
        }));
        // Store market rate for profit calculation
        setMarketRate(rateData.rate || rateData.buyRate);
      }
    }
  }, [usdCurrency, iqdCurrency, exchangeRates, mode]);

  const fetchCurrencies = async () => {
    try {
      const response = await currencyService.getCurrencies(true);
      if (response.success) {
        const usd = response.data.find(c => c.code === 'USD');
        const iqd = response.data.find(c => c.code === 'IQD');
        setUsdCurrency(usd);
        setIqdCurrency(iqd);
      }
    } catch (error) {
      console.error('Failed to fetch currencies:', error);
    }
  };

  const fetchExchangeRates = async () => {
    try {
      const response = await currencyService.getExchangeRates();
      if (response.success) {
        setExchangeRates(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      customerName: '',
      customerPhone: '',
      rate: '',
      amount: '',
      notes: ''
    });
    setMode('sell');
    setInputMode('give');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Calculate what customer gets or needs to give
  const calculateResult = () => {
    const rate = parseFloat(formData.rate);
    const amount = parseFloat(formData.amount);

    if (isNaN(rate) || isNaN(amount) || rate <= 0 || amount <= 0) {
      return null;
    }

    if (mode === 'sell') {
      // Customer sells USD to us
      if (inputMode === 'give') {
        // They enter USD they give -> calculate IQD they get
        return amount * rate;
      } else {
        // They enter IQD they want -> calculate USD they need to give
        return amount / rate;
      }
    } else {
      // Customer buys USD from us
      if (inputMode === 'give') {
        // They enter IQD they give -> calculate USD they get
        return amount / rate;
      } else {
        // They enter USD they want -> calculate IQD they need to give
        return amount * rate;
      }
    }
  };

  const result = calculateResult();

  const handleSubmit = async (e) => {
    e.preventDefault();

    const rate = parseFloat(formData.rate);
    const amount = parseFloat(formData.amount);

    if (!rate || rate <= 0) {
      toast.error(t('validation.positiveNumber') || 'Please enter a valid rate');
      return;
    }
    if (!amount || amount <= 0) {
      toast.error(t('validation.positiveNumber') || 'Please enter a valid amount');
      return;
    }
    if (!usdCurrency || !iqdCurrency) {
      toast.error(t('common.error') || 'Currency data not loaded');
      return;
    }

    setLoading(true);
    try {
      let payload;

      if (mode === 'sell') {
        // Customer sells USD to us (gives USD, gets IQD)
        // We RECEIVE USD, we GIVE IQD
        let usdAmount, iqdAmount;

        if (inputMode === 'give') {
          // User entered USD they give
          usdAmount = amount;
          iqdAmount = amount * rate;
        } else {
          // User entered IQD they want
          iqdAmount = amount;
          usdAmount = amount / rate;
        }

        payload = {
          customerName: formData.customerName.trim() || null,
          customerPhone: formData.customerPhone.trim() || null,
          currencyInId: usdCurrency.id,
          currencyOutId: iqdCurrency.id,
          amountIn: usdAmount,
          amountOut: iqdAmount,
          exchangeRate: rate,
          marketRate: marketRate || rate,
          notes: formData.notes.trim() || null
        };
      } else {
        // Customer buys USD from us (gives IQD, gets USD)
        // We RECEIVE IQD, we GIVE USD
        let usdAmount, iqdAmount;

        if (inputMode === 'give') {
          // User entered IQD they give
          iqdAmount = amount;
          usdAmount = amount / rate;
        } else {
          // User entered USD they want
          usdAmount = amount;
          iqdAmount = amount * rate;
        }

        payload = {
          customerName: formData.customerName.trim() || null,
          customerPhone: formData.customerPhone.trim() || null,
          currencyInId: iqdCurrency.id,
          currencyOutId: usdCurrency.id,
          amountIn: iqdAmount,
          amountOut: usdAmount,
          exchangeRate: rate,
          marketRate: marketRate || rate,
          notes: formData.notes.trim() || null
        };
      }

      const response = await transactionService.createTransaction(payload);
      if (response.success) {
        toast.success(t('transactions.transactionCreated') || 'Transaction created successfully');
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.error') || 'Failed to create transaction');
      console.error('Failed to create transaction:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(num);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('transactions.newTransaction')}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Mode Toggle */}
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setMode('sell')}
            className={`relative p-4 rounded-xl border-2 transition-all ${mode === 'sell'
              ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
              }`}
          >
            <div className="text-center">
              <div className={`text-2xl mb-1 ${mode === 'sell' ? 'text-green-600' : 'text-gray-400'}`}>
                ↓
              </div>
              <div className={`font-semibold ${mode === 'sell' ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-300'}`}>
                {t('transactions.customerSellsUSD')}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('transactions.weReceiveUSD')}
              </div>
            </div>
            {mode === 'sell' && (
              <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full"></div>
            )}
          </button>

          <button
            type="button"
            onClick={() => setMode('buy')}
            className={`relative p-4 rounded-xl border-2 transition-all ${mode === 'buy'
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
              }`}
          >
            <div className="text-center">
              <div className={`text-2xl mb-1 ${mode === 'buy' ? 'text-blue-600' : 'text-gray-400'}`}>
                ↑
              </div>
              <div className={`font-semibold ${mode === 'buy' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'}`}>
                {t('transactions.customerBuysUSD')}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('transactions.weGiveUSD')}
              </div>
            </div>
            {mode === 'buy' && (
              <div className="absolute top-2 right-2 w-3 h-3 bg-blue-500 rounded-full"></div>
            )}
          </button>
        </div>

        {/* Rate Input */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('transactions.exchangeRate')} (1 {t('common.usd')} = ? {t('common.iqd')})
          </label>
          <div className="flex items-center gap-3">
            <span className="text-lg font-bold text-gray-600 dark:text-gray-300">1 {t('common.usd')}</span>
            <span className="text-gray-400">=</span>
            <input
              type="number"
              step="0.5"
              min="0"
              value={formData.rate}
              onChange={(e) => handleChange('rate', e.target.value)}
              className="flex-1 px-4 py-3 text-xl font-bold text-center border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="1450"
            />
            <span className="text-lg font-bold text-gray-600 dark:text-gray-300">{t('common.iqd')}</span>
          </div>
        </div>

        {/* Input Mode Toggle */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setInputMode('give')}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${inputMode === 'give'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
          >
            {t('common.enterWhatCustomerGives')}
          </button>
          <button
            type="button"
            onClick={() => setInputMode('receive')}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${inputMode === 'receive'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
          >
            {t('common.enterWhatCustomerWants')}
          </button>
        </div>

        {/* Amount Input */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {inputMode === 'give' ? (
              mode === 'sell'
                ? t('transactions.customerGivesUSD')
                : t('transactions.customerGivesIQD')
            ) : (
              mode === 'sell'
                ? (t('common.customerWants') + ' (' + t('common.iqd') + ')')
                : (t('common.customerWants') + ' (' + t('common.usd') + ')')
            )}
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              step={
                (mode === 'sell' && inputMode === 'give') || (mode === 'buy' && inputMode === 'receive')
                  ? '1'
                  : '1000'
              }
              min="0"
              value={formData.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              className="flex-1 px-4 py-3 text-xl font-bold text-center border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder={
                (mode === 'sell' && inputMode === 'give') || (mode === 'buy' && inputMode === 'receive')
                  ? '100'
                  : '145000'
              }
            />
            <span className="text-lg font-bold text-gray-600 dark:text-gray-300 w-12">
              {(mode === 'sell' && inputMode === 'give') || (mode === 'buy' && inputMode === 'receive')
                ? t('common.usd')
                : t('common.iqd')}
            </span>
          </div>
        </div>

        {/* Result Display */}
        {result !== null && (
          <div className={`rounded-xl p-6 ${mode === 'sell'
            ? 'bg-green-100 dark:bg-green-900/40 border-2 border-green-300 dark:border-green-700'
            : 'bg-blue-100 dark:bg-blue-900/40 border-2 border-blue-300 dark:border-blue-700'
            }`}>
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                {inputMode === 'give'
                  ? t('transactions.customerGets')
                  : t('common.customerNeedsToGive')
                }
              </p>
              <p className={`text-4xl font-bold ${mode === 'sell' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'
                }`}>
                {inputMode === 'give' ? (
                  mode === 'sell'
                    ? formatNumber(result, 0)
                    : formatNumber(result, 2)
                ) : (
                  mode === 'sell'
                    ? formatNumber(result, 2)
                    : formatNumber(result, 0)
                )} {inputMode === 'give' ? (mode === 'sell' ? t('common.iqd') : t('common.usd')) : (mode === 'sell' ? t('common.usd') : t('common.iqd'))}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 font-mono">
                {mode === 'sell' ? (
                  <>{formatNumber(parseFloat(formData.amount), 2)} {t('common.usd')} × {formData.rate} = {formatNumber(result, 0)} {t('common.iqd')}</>
                ) : (
                  <>{formatNumber(parseFloat(formData.amount), 0)} {t('common.iqd')} ÷ {formData.rate} = {formatNumber(result, 2)} {t('common.usd')}</>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Customer Info (Collapsible/Optional) */}
        <details className="group">
          <summary className="cursor-pointer text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200">
            + {t('transactions.addCustomerInfo')}
          </summary>
          <div className="mt-3 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('transactions.customerName')}
              </label>
              <input
                type="text"
                value={formData.customerName}
                onChange={(e) => handleChange('customerName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder={t('transactions.optional')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('transactions.customerPhone')}
              </label>
              <input
                type="text"
                value={formData.customerPhone}
                onChange={(e) => handleChange('customerPhone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder={t('transactions.optional')}
              />
            </div>
          </div>
        </details>

        {/* Notes */}
        <details className="group">
          <summary className="cursor-pointer text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200">
            + {t('transactions.addNotes')}
          </summary>
          <div className="mt-3">
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder={t('transactions.notesPlaceholder')}
              rows={2}
            />
          </div>
        </details>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
            className="flex-1"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            loading={loading}
            disabled={!result}
            className={`flex-1 ${mode === 'sell'
              ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
              : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
              }`}
          >
            {t('transactions.confirmTransaction')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default TransactionForm;
